import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface OrderCancellationActor {
  /** Who cancelled the order — typically an email, or a system label such as
   *  "System (EAPI)". Null when no source records the actor. */
  changedBy: string | null;
  /** When the order was cancelled; falls back to the caller-supplied
   *  `canceled_at` when no source has a timestamp. */
  changedAt: string | null;
  /** Set when the order was retired by the Duplicate Orders merge rather than
   *  a real cancellation. */
  mergedInto: { id: string; uniqueId: string | null } | null;
}

export interface OrderMergeInfo {
  mergedIntoOrderId: string | null | undefined;
  mergedAt: string | null | undefined;
  mergedBy: string | null | undefined;
}

/** Recovers *who* cancelled an order and *when*, trying in order:
 *
 *  1. The latest `order_change_log` row that set `status` to `canceled`.
 *  2. The latest `change_events` row with `event_type = 'trip_cancelled'`
 *     (written by CancelOrderDialog, which does not always leave a change-log
 *     row).
 *  3. The order's merge columns: `merge_orders` sets `status = 'canceled'`
 *     directly and moves every change-log row to the kept order, so a merged
 *     duplicate has neither of the above. The actor is `orders.merged_by`
 *     (a user id), resolved to an email through `user_directory`.
 *
 *  The last fallback is the order's own `canceled_at`. */
export function useOrderCancelledBy(
  orderId: string | undefined | null,
  canceledAt: string | null | undefined,
  enabled: boolean = true,
  merge?: OrderMergeInfo,
) {
  const mergedIntoOrderId = merge?.mergedIntoOrderId ?? null;
  const mergedBy = merge?.mergedBy ?? null;
  return useQuery<OrderCancellationActor>({
    queryKey: ["order-cancelled-by", orderId, mergedIntoOrderId, mergedBy],
    enabled: !!orderId && enabled,
    queryFn: async () => {
      const { data: logRow, error: logError } = await supabase
        .from("order_change_log")
        .select("changed_by, changed_at")
        .eq("order_id", orderId!)
        .eq("field_name", "status")
        .eq("new_value", "canceled")
        .order("changed_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (logError) throw logError;
      if (logRow) {
        return {
          changedBy: logRow.changed_by ?? null,
          changedAt: logRow.changed_at ?? canceledAt ?? null,
          mergedInto: null,
        };
      }

      const { data: eventRow, error: eventError } = await supabase
        .from("change_events")
        .select("created_by_email, created_at")
        .eq("order_id", orderId!)
        .eq("event_type", "trip_cancelled")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (eventError) throw eventError;
      if (eventRow) {
        return {
          changedBy: eventRow.created_by_email ?? null,
          changedAt: eventRow.created_at ?? canceledAt ?? null,
          mergedInto: null,
        };
      }

      if (mergedIntoOrderId) {
        const { data: kept, error: keptError } = await supabase
          .from("orders")
          .select("unique_id")
          .eq("id", mergedIntoOrderId)
          .maybeSingle();
        if (keptError) throw keptError;
        let mergerEmail: string | null = null;
        if (mergedBy) {
          // RLS limits user_directory to users sharing a tenant; a miss just
          // leaves the actor unknown.
          const { data: merger } = await supabase
            .from("user_directory")
            .select("email")
            .eq("id", mergedBy)
            .maybeSingle();
          mergerEmail = merger?.email ?? null;
        }
        return {
          changedBy: mergerEmail,
          changedAt: merge?.mergedAt ?? canceledAt ?? null,
          mergedInto: { id: mergedIntoOrderId, uniqueId: kept?.unique_id ?? null },
        };
      }

      return { changedBy: null, changedAt: canceledAt ?? null, mergedInto: null };
    },
  });
}

/** Converts a `changed_by` value (typically an email) into a short
 *  "@username" handle. Returns "@unknown" for empty input. */
export function toUsernameHandle(changedBy: string | null | undefined): string {
  if (!changedBy) return "@unknown";
  const trimmed = changedBy.trim();
  if (!trimmed) return "@unknown";
  // System actors like "System (EAPI)" — show as-is, no @ prefix.
  if (/\s/.test(trimmed) && !trimmed.includes("@")) return trimmed;
  const local = trimmed.includes("@") ? trimmed.split("@")[0] : trimmed;
  return `@${local}`;
}